import { ref, readonly } from 'vue'
import { useCountryDetection } from './useCountryDetection'
import { findPhoneCodeByIsoCodeWithDefault } from '@/composables/phone/detect/usePhoneCodeFromCountry'
import type { PhoneCode } from '@/constants/phone-codes'

/**
 * Глобальный стор для хранения определенной страны клиента
 * Используется для автодетекта телефонного кода в формах
 */
const countryIsoCode = ref<string | null>(null)
const countryPhoneCode = ref<PhoneCode | null>(null)
const isDetecting = ref(false)
const isDetected = ref(false)

/**
 * Composable для работы с глобальным стором страны
 * @returns Реактивные refs и методы для работы со страной
 */
export function useCountryStore() {
  const { detectCountry, resetDetection: resetCountryDetection } = useCountryDetection()

  /**
   * Определяет страну клиента и сохраняет в стор
   * Вызывается один раз при инициализации приложения
   * @param useApiFallback - Использовать API запрос как fallback, если CDN заголовки недоступны
   */
  const detectAndStoreCountry = async (useApiFallback: boolean = true): Promise<void> => {
    if (isDetected.value || isDetecting.value) {
      return
    }

    isDetecting.value = true

    try {
      const detectedIsoCode = await detectCountry(useApiFallback)
      countryIsoCode.value = detectedIsoCode

      if (detectedIsoCode) {
        // Находим PhoneCode по ISO коду
        const phoneCode = findPhoneCodeByIsoCodeWithDefault(detectedIsoCode, '380')
        countryPhoneCode.value = phoneCode

      } else {
        // Если страна не определена, используем дефолт
        const defaultPhoneCode = findPhoneCodeByIsoCodeWithDefault(null, '380')
        countryPhoneCode.value = defaultPhoneCode
      }

      isDetected.value = true
    } catch (error) {
      console.error('[useCountryStore] Ошибка при определении страны:', error)
      // В случае ошибки используем дефолт
      const defaultPhoneCode = findPhoneCodeByIsoCodeWithDefault(null, '380')
      countryPhoneCode.value = defaultPhoneCode
      isDetected.value = true
    } finally {
      isDetecting.value = false
    }
  }

  /**
   * Сбрасывает определенную страну
   * Полезно для тестирования или принудительного переопределения
   */
  const resetCountry = () => {
    countryIsoCode.value = null
    countryPhoneCode.value = null
    isDetected.value = false
    isDetecting.value = false
    resetCountryDetection()
  }

  /**
   * Устанавливает страну вручную.
   * Также вызывается из useAppInit при fallback API (аккаунт по detect-by-country → UA).
   * @param isoCode - ISO код страны
   */
  const setCountry = (isoCode: string | null) => {
    countryIsoCode.value = isoCode
    if (isoCode) {
      const phoneCode = findPhoneCodeByIsoCodeWithDefault(isoCode, '380')
      countryPhoneCode.value = phoneCode
    } else {
      const defaultPhoneCode = findPhoneCodeByIsoCodeWithDefault(null, '380')
      countryPhoneCode.value = defaultPhoneCode
    }
    isDetected.value = true
  }

  return {
    // Только для чтения
    countryIsoCode: readonly(countryIsoCode),
    countryPhoneCode: readonly(countryPhoneCode),
    isDetecting: readonly(isDetecting),
    isDetected: readonly(isDetected),
    // Методы
    detectAndStoreCountry,
    resetCountry,
    setCountry,
  }
}
